import React, { useState, useEffect } from 'react';
import { theme, Upload } from 'antd';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import {
  useSortable,
  verticalListSortingStrategy,
  SortableContext
} from '@dnd-kit/sortable';
import { DndContext, PointerSensor, useSensor } from '@dnd-kit/core';
import { UploadOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import { evidencesOp } from 'store/ducks/Evidences';
import { IEvidenceToShow } from 'store/ducks/Evidences/types';
import Helpers from 'utils/Helpers';
import ApiService from 'utils/ApiCalls';
import * as Styled from './CustomFileUpload.styled';

const { useToken } = theme;

// File helper functions
const processFileForDisplay = (file: UploadFile): UploadFile => {
  return {
    ...file,
    uid: file.uid || `file-${Date.now()}`,
    status: file.status || 'done',
    url:
      file.url ||
      file.response?.url ||
      file.response?.file_url ||
      file.response?.path
  };
};

const getImageSrc = (file: UploadFile): string | null => {
  // Priority: server URL > blob URL > null
  if (file.url) {
    return file.url;
  }
  if (file.originFileObj instanceof Blob) {
    return URL.createObjectURL(file.originFileObj);
  }
  return null;
};

interface DraggableUploadListItemProps {
  originNode: React.ReactElement<
    any,
    string | React.JSXElementConstructor<any>
  >;
  file: UploadFile<any>;
  evidenceId?: string | number;
}

const DraggableUploadListItem = ({
  originNode,
  file,
  evidenceId
}: DraggableUploadListItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: file.uid
  });

  const imageSrc = getImageSrc(file);
  const [showImage, setShowImage] = useState(true);
  const isImageFile = file.type?.startsWith('image/');

  const handleFileClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    // Don't download if clicking delete button
    if ((e.target as HTMLElement).closest('.anticon-delete')) {
      return;
    }

    if (evidenceId) {
      try {
        const response = await ApiService.downloadEvidenceFile(evidenceId);
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download failed:', error);
      }
    }
  };

  return (
    <Styled.DraggableEl
      ref={setNodeRef}
      isDragging={isDragging}
      transition={transition}
      transform={transform}
    >
      <div
        {...attributes}
        {...listeners}
        onClick={!isImageFile ? handleFileClick : undefined}
      >
        <Styled.FlexContainer>
          <div>{originNode}</div>
        </Styled.FlexContainer>
      </div>
      {/* Always try to show image, hide on error */}
      {imageSrc && showImage && (
        <Styled.StyledImage
          src={imageSrc}
          alt={file.name || 'Uploaded image'}
          title={file.name || 'Uploaded image'}
          onClick={handleFileClick}
          onError={() => setShowImage(false)}
        />
      )}
    </Styled.DraggableEl>
  );
};

interface ICustomFileUploadProps {
  defaultFileList: (UploadFile | string)[];
  onChange?: (fileList: UploadFile[]) => void;
  evidenceId?: string | number;
  evidenceData?: IEvidenceToShow;
}

const CustomFileUpload: React.FC<ICustomFileUploadProps> = ({
  defaultFileList,
  onChange: onFileChange,
  evidenceId,
  evidenceData
}) => {
  const { token } = useToken();
  const dispatch = useDispatch();

  const [fileList, setFileList] = useState<UploadFile<string>[]>([]);

  // Initialize fileList with defaultFileList on component mount
  useEffect(() => {
    if (defaultFileList && defaultFileList.length > 0) {
      const processedFiles = defaultFileList.map((file, index) => {
        if (typeof file === 'string') {
          const fileName = file.split('/').pop() || `file-${index}`;
          const fileExtension = fileName.split('.').pop()?.toLowerCase();
          return {
            uid: `default-${index}`,
            name: fileName,
            status: 'done' as const,
            url: file,
            type: fileExtension ? `application/${fileExtension}` : undefined
          };
        }
        return processFileForDisplay({
          ...file,
          uid: file.uid || `default-${index}`
        });
      });
      setFileList(processedFiles);
    } else {
      setFileList([]);
    }
  }, [defaultFileList]);

  const sensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 }
  });

  const onChange: UploadProps['onChange'] = async ({
    fileList: newFileList
  }) => {
    if (newFileList) {
      setFileList(newFileList);

      // Upload new files to API
      const newFiles = newFileList.filter(
        (file) => file.originFileObj && file.status !== 'done'
      );

      for (const file of newFiles) {
        if (file.originFileObj) {
          try {
            const formData = new FormData();
            formData.append('file', file.originFileObj);

            const response = await ApiService.uploadEvidenceFiles(formData);

            file.status = 'done';
            file.response = response.data;
            // Ensure the URL is properly set for image display
            file.url =
              response.data?.url ||
              response.data?.file_url ||
              response.data?.path;
            file.name =
              response.data?.filename || response.data?.name || file.name;
          } catch (error) {
            file.status = 'error';
            console.error('File upload failed:', error);
          }
        }
      }

      // Update the file list with the uploaded file information
      const updatedFileList = newFileList.map((file) =>
        processFileForDisplay({
          ...file,
          url:
            file.url ||
            file.response?.url ||
            file.response?.file_url ||
            file.response?.path
        })
      );

      setFileList(updatedFileList);
      onFileChange?.(updatedFileList);

      // Update evidence data with uploaded files if evidenceId is provided
      if (evidenceId && evidenceData) {
        dispatch(
          evidencesOp.editEvidenceValue(
            evidenceId,
            evidenceData,
            'evd_file',
            JSON.stringify(updatedFileList)
          )
        );
      }

      dispatch(complianceEstOp.setSaveDisabled(false));
    }
  };

  return (
    <Styled.UploadFileWrapper
      token={token}
      fileListExist={fileList.length !== 0}
    >
      <DndContext
        sensors={[sensor]}
        onDragEnd={({ active, over }: any) =>
          Helpers.onDragEnd(
            {
              active,
              over
            },
            setFileList,
            'uid'
          )
        }
      >
        <SortableContext
          items={fileList.map((i) => i.uid)}
          strategy={verticalListSortingStrategy}
        >
          <Upload
            fileList={fileList}
            multiple
            onChange={onChange}
            beforeUpload={() => false}
            itemRender={(originNode, file) => (
              <DraggableUploadListItem
                originNode={originNode}
                file={file}
                evidenceId={evidenceId}
              />
            )}
          >
            <Styled.StyledButton
              type='primary'
              token={token}
              icon={<UploadOutlined />}
            >
              Click to Upload
            </Styled.StyledButton>
          </Upload>
        </SortableContext>
      </DndContext>
    </Styled.UploadFileWrapper>
  );
};

export default CustomFileUpload;
